import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Lock, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AUDIT_LOGS_QUERY,
  SET_AUDIT_PIN_MUTATION,
  START_STEP_UP_AUDIT_SESSION_MUTATION,
} from './audit.gql';

import { useAuditStepUpStore } from '@/stores/auditStepUpStore';
import { TotemMark } from '@/shared/brand/TotemLogo';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface AuditLog {
  id: string;
  actor_user_id: string | null;
  actor_role: string | null;
  workstation_id: string | null;
  module: string;
  action: string;
  reference_kind: string | null;
  reference_id: string | null;
  amount_cents: string | null;
  reason: string | null;
  previous_state_json: string | null;
  new_state_json: string | null;
  metadata_json: string | null;
  correlation_id: string | null;
  ip: string | null;
  created_at: string;
}

interface AuditLogsResponse {
  auditLogs: { items: AuditLog[]; page: { total: number; has_more: boolean } };
}

export function AuditPage(): JSX.Element {
  const { t } = useTranslation();
  const get_token = useAuditStepUpStore((s) => s.getActiveToken);
  const set_token = useAuditStepUpStore((s) => s.setToken);
  const clear_token = useAuditStepUpStore((s) => s.clear);

  const [active_token, setActiveTokenLocal] = useState<string | null>(get_token());
  const [show_pin, setShowPin] = useState(active_token === null);
  const [show_set_pin, setShowSetPin] = useState(false);
  const [filter_module, setFilterModule] = useState('');
  const [filter_action, setFilterAction] = useState('');
  const [filter_actor, setFilterActor] = useState('');
  const [error_text, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    const tick = setInterval(() => {
      const t_now = get_token();
      if (t_now !== active_token) {
        setActiveTokenLocal(t_now);
        if (!t_now) setShowPin(true);
      }
    }, 30_000);
    return () => clearInterval(tick);
  }, [active_token, get_token]);

  const { data, refetch } = useQuery<AuditLogsResponse>(AUDIT_LOGS_QUERY, {
    variables: {
      offset: 0,
      limit: 100,
      module: filter_module || null,
      action: filter_action || null,
      actor_user_id: filter_actor || null,
    },
    fetchPolicy: 'network-only',
    skip: !active_token,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    context: active_token ? { headers: { 'X-Audit-Step-Up': active_token } } : undefined,
  });

  const items = data?.auditLogs.items ?? [];

  const onLogout = useCallback((): void => {
    clear_token();
    setActiveTokenLocal(null);
    setShowPin(true);
  }, [clear_token]);

  const onUnlocked = useCallback(
    (token: string, expires_in_seconds: number): void => {
      set_token(token, expires_in_seconds);
      setActiveTokenLocal(token);
      setShowPin(false);
      setErrorText(null);
    },
    [set_token],
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{t('audit.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('audit.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowSetPin(true)}>
            <ShieldCheck className="h-4 w-4" aria-hidden />
            <span>{t('audit.set_pin')}</span>
          </Button>
          {active_token ? (
            <Button type="button" variant="ghost" size="sm" onClick={onLogout}>
              <Lock className="h-4 w-4" aria-hidden />
              <span>{t('audit.relock')}</span>
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={() => setShowPin(true)}>
              <ShieldCheck className="h-4 w-4" aria-hidden />
              <span>{t('audit.unlock')}</span>
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void refetch();
            }}
            disabled={!active_token}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </header>

      {error_text ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error_text}
        </div>
      ) : null}

      {!active_token ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {t('audit.locked_body')}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="grid gap-3 pt-4 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_module')}</Label>
                <Input value={filter_module} onChange={(e) => setFilterModule(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_action')}</Label>
                <Input value={filter_action} onChange={(e) => setFilterAction(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_actor')}</Label>
                <div className="flex gap-2">
                  <Input value={filter_actor} onChange={(e) => setFilterActor(e.target.value)} />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void refetch();
                    }}
                  >
                    <Search className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {items.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-2 py-6 text-center text-sm text-muted-foreground">
                <TotemMark className="h-10 w-auto text-muted-foreground/40" aria-hidden />
                {t('audit.empty')}
              </CardContent>
            </Card>
          ) : (
            <ul className="space-y-2">
              {items.map((row) => (
                <li key={row.id}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Badge variant="muted">{row.module}</Badge>
                          <span className="font-mono">{row.action}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {fmtDate(row.created_at)}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 text-xs">
                      <div className="grid grid-cols-2 gap-1">
                        <div>
                          <span className="text-muted-foreground">{t('audit.actor')}: </span>
                          <span className="font-mono">{row.actor_user_id ?? '—'}</span>
                          {row.actor_role ? (
                            <Badge variant="muted" className="ml-2">
                              {row.actor_role}
                            </Badge>
                          ) : null}
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('audit.reference')}: </span>
                          <span className="font-mono">
                            {row.reference_kind}#{row.reference_id?.slice(0, 8) ?? '—'}
                          </span>
                        </div>
                        {row.amount_cents ? (
                          <div>
                            <span className="text-muted-foreground">{t('audit.amount')}: </span>
                            <span className="font-mono tabular-nums">{row.amount_cents}</span>
                          </div>
                        ) : null}
                        {row.workstation_id ? (
                          <div>
                            <span className="text-muted-foreground">{t('audit.workstation')}: </span>
                            <span className="font-mono">{row.workstation_id.slice(0, 8)}</span>
                          </div>
                        ) : null}
                      </div>
                      {row.reason ? (
                        <p className="text-muted-foreground">{row.reason}</p>
                      ) : null}
                      {row.new_state_json || row.previous_state_json ? (
                        <details className="text-[10px] text-muted-foreground">
                          <summary>{t('audit.payload')}</summary>
                          {row.previous_state_json ? (
                            <pre className="overflow-auto rounded bg-muted p-2">
                              prev: {row.previous_state_json}
                            </pre>
                          ) : null}
                          {row.new_state_json ? (
                            <pre className="overflow-auto rounded bg-muted p-2">
                              new: {row.new_state_json}
                            </pre>
                          ) : null}
                        </details>
                      ) : null}
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {show_pin ? (
        <StepUpPinDialog
          on_close={() => setShowPin(false)}
          on_unlocked={onUnlocked}
          on_error={setErrorText}
        />
      ) : null}

      {show_set_pin ? (
        <SetAuditPinDialog
          on_close={() => setShowSetPin(false)}
          on_done={() => {
            setShowSetPin(false);
            setErrorText(t('audit.pin_set_ok'));
          }}
          on_error={setErrorText}
        />
      ) : null}
    </div>
  );
}

function StepUpPinDialog({
  on_close,
  on_unlocked,
  on_error,
}: {
  on_close: () => void;
  on_unlocked: (token: string, expires_in_seconds: number) => void;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [start, { loading }] = useMutation<{
    startStepUpAuditSession: { audit_token: string; expires_in_seconds: number };
  }>(START_STEP_UP_AUDIT_SESSION_MUTATION);

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    try {
      const r = await start({ variables: { input: { pin } } });
      const payload = r.data?.startStepUpAuditSession;
      if (!payload) throw new Error('no_payload');
      on_unlocked(payload.audit_token, payload.expires_in_seconds);
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'audit_pin_invalid') return on_error(t('audit.error_pin_invalid'));
        if (code === 'audit_pin_not_set') return on_error(t('audit.error_pin_not_set'));
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('audit.unlock_title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('audit.unlock_hint')}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <Label htmlFor="audit-pin">{t('audit.pin_label')}</Label>
            <Input
              id="audit-pin"
              type="password"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={loading}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={pin.length === 0 || loading}
          >
            {loading ? t('common.loading') : t('audit.unlock')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function SetAuditPinDialog({
  on_close,
  on_done,
  on_error,
}: {
  on_close: () => void;
  on_done: () => void;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [setAuditPin, { loading }] = useMutation(SET_AUDIT_PIN_MUTATION);

  const can_submit = pin.length >= 4 && pin === confirm && !loading;

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    try {
      await setAuditPin({ variables: { input: { pin } } });
      on_done();
    } catch (err) {
      if (err instanceof ApolloError) {
        return on_error(err.graphQLErrors[0]?.message ?? err.message);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('audit.set_pin_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t('audit.pin_label')}</Label>
            <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} disabled={loading} />
          </div>
          <div className="space-y-1">
            <Label>{t('audit.pin_confirm')}</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
            />
            {pin && confirm && pin !== confirm ? (
              <p className="text-xs text-destructive">{t('audit.error_pin_mismatch')}</p>
            ) : null}
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('audit.set_pin')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}
